import React, { Suspense, lazy } from 'react';
import { createPortal } from 'react-dom';
import { Toaster } from 'react-hot-toast';
import { useDocumentPip } from '@/hooks/useDocumentPip';
import { Login } from '@/components/Login';
import { TopNav } from '@/components/TopNav';
import { HorizontalCategoryBar } from '@/components/HorizontalCategoryBar';
import { ResellerLanding } from '@/components/ResellerLanding';
import { xtreamAPI } from '@/services/xtream-api';
import { ErrorBoundary, VideoErrorBoundary } from '@/components/ErrorBoundary';
import { useAppStore } from '@/store/app-store';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { useDataPreloader } from '@/hooks/useDataPreloader';
import { syncAccentColor } from '@/utils/accent';
import { serverTzOffsetMinutes } from '@/utils/epg-time';
import { Loader2 } from 'lucide-react';

// Code-split heavy / route-level surfaces. VideoPlayer in particular pulls in
// hls.js (~520 kB) which should not be in the initial bundle.
const named = <T extends Record<string, any>>(p: Promise<T>, key: keyof T) =>
  p.then((m) => ({ default: m[key] }));

const Home = lazy(() => named(import('@/components/Home'), 'Home'));
const ChannelList = lazy(() => named(import('@/components/ChannelList'), 'ChannelList'));
const SeriesList = lazy(() => named(import('@/components/SeriesList'), 'SeriesList'));
const EPGGrid = lazy(() => named(import('@/components/EPG/EPGGrid'), 'EPGGrid'));
const EPGOverlay = lazy(() => named(import('@/components/EPG/EPGOverlay'), 'EPGOverlay'));
const VideoPlayer = lazy(() => named(import('@/components/VideoPlayer'), 'VideoPlayer'));
const Settings = lazy(() => named(import('@/components/Settings'), 'Settings'));
const Favorites = lazy(() => named(import('@/components/Favorites'), 'Favorites'));
const MovieDetailModal = lazy(() => named(import('@/components/MovieDetailModal'), 'MovieDetailModal'));
const GlobalSearch = lazy(() => named(import('@/components/GlobalSearch'), 'GlobalSearch'));
const SubscribeGate = lazy(() => named(import('@/components/SubscribeGate'), 'SubscribeGate'));
const AdminPanel = lazy(() => named(import('@/components/AdminPanel'), 'AdminPanel'));
const MultiViewGrid = lazy(() => named(import('@/components/MultiViewGrid'), 'MultiViewGrid'));

const ViewFallback = () => (
  <div className="flex items-center justify-center h-full">
    <Loader2 className="animate-spin text-primary-500" size={40} />
  </div>
);

function App() {
  const {
    isAuthenticated,
    setCredentials,
    setAuthInfo,
    authInfo,
    epgOffsetAuto,
    autoSetEpgOffset,
    currentStream,
    currentStreamUrl,
    setCurrentStream,
    theme,
    currentView,
    setCurrentView,
    accentColor,
    epgOverlayOpen,
    setEpgOverlayOpen,
    multiViewStreams,
    showMultiView,
    setShowMultiView,
    removeMultiViewStream,
    getResumePosition,
    setResumePosition,
    clearResumePosition,
    globalSearchOpen,
    setGlobalSearchOpen,
    detailVod,
    subscribeGateOpen,
    adminPanelOpen,
    isGuest,
    loginRequested,
    setLoginRequested,
    serverUrl,
    brandName,
    subscribeUrl,
    resellerMode,
  } = useAppStore();

  // Apply the (persisted/operator-edited) provider URL to the API client.
  React.useEffect(() => {
    if (serverUrl) xtreamAPI.setServerUrl(serverUrl);
  }, [serverUrl]);

  // Apply the accent color (themes the whole `primary` palette).
  React.useEffect(() => {
    syncAccentColor(accentColor);
  }, [accentColor]);

  // Auto-derive the EPG/catch-up timezone offset from the provider's server clock
  // (unless the operator has set it manually).
  React.useEffect(() => {
    if (!epgOffsetAuto) return;
    const off = serverTzOffsetMinutes(authInfo?.server_info);
    if (off != null) autoSetEpgOffset(off);
  }, [authInfo, epgOffsetAuto, autoSetEpgOffset]);

  // Silent re-login on reload: the store persists credentials but the API singleton
  // starts empty, so re-authenticate once on startup instead of dropping to Login.
  const [booting, setBooting] = React.useState<boolean>(() => {
    const s = useAppStore.getState();
    return !!s.credentials && !s.isAuthenticated && !s.isGuest;
  });
  const bootRan = React.useRef(false);
  React.useEffect(() => {
    if (bootRan.current) return;
    bootRan.current = true;
    const s = useAppStore.getState();
    const creds = s.credentials;
    if (!creds || s.isAuthenticated || s.isGuest) {
      setBooting(false);
      return;
    }
    (async () => {
      try {
        xtreamAPI.setServerUrl(s.serverUrl || creds.serverUrl);
        const ok = await xtreamAPI.setCredentials(creds);
        if (ok) {
          const info = xtreamAPI.getUserInfo();
          if (info) setAuthInfo(info);
          setCredentials(creds);
        }
      } catch (e) {
        console.error('Auto re-login failed:', e);
      } finally {
        setBooting(false);
      }
    })();
  }, [setAuthInfo, setCredentials]);

  // Resume key for the active stream (live streams never resume). Normalized to
  // 'vod'/'series' so it matches the keys used by watch history & Continue Watching
  // (raw VOD stream_type is usually "movie").
  const resumeKey =
    currentStream && currentStream.stream_type !== 'live'
      ? `${currentStream.stream_type === 'series' ? 'series' : 'vod'}:${currentStream.stream_id}`
      : null;

  const playerStartTime = resumeKey ? getResumePosition(resumeKey) ?? 0 : 0;

  const handleProgress = React.useCallback(
    (time: number, durationSecs: number) => {
      if (resumeKey) setResumePosition(resumeKey, time, durationSecs);
    },
    [resumeKey, setResumePosition]
  );

  const handleEnded = React.useCallback(() => {
    if (resumeKey) clearResumePosition(resumeKey);
    // Auto-play next episode if the series view registered a handler.
    const next = useAppStore.getState().nextEpisodeHandler;
    if (next) next();
  }, [resumeKey, clearResumePosition]);

  // Mini-player dock: the player keeps playing while you browse. 'inline' = the
  // full-width player at the top of the view; 'mini' = a small draggable floating
  // window. A new stream always restores the full inline view.
  const [playerDock, setPlayerDock] = React.useState<'inline' | 'mini'>('inline');
  const [miniPos, setMiniPos] = React.useState<{ x: number; y: number } | null>(null);
  const playerBoxRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    setPlayerDock('inline');
    setMiniPos(null);
  }, [currentStreamUrl]);

  // Document Picture-in-Picture: a pop-out window (over other tabs) with our
  // controls. Falls back to native PiP inside VideoPlayer when unsupported.
  const docPip = useDocumentPip();
  const handlePopOut = React.useCallback(() => {
    setPlayerDock('inline');
    docPip.open({ width: 640, height: 380 });
  }, [docPip]);
  // Close the pop-out if the player is closed.
  React.useEffect(() => {
    if (!currentStream && docPip.pipWindow) docPip.close();
  }, [currentStream, docPip]);

  const startMiniDrag = (e: React.PointerEvent) => {
    if (playerDock !== 'mini' || !playerBoxRef.current) return;
    const rect = playerBoxRef.current.getBoundingClientRect();
    const offX = e.clientX - rect.left;
    const offY = e.clientY - rect.top;
    const onMove = (ev: PointerEvent) => {
      const x = Math.max(8, Math.min(window.innerWidth - rect.width - 8, ev.clientX - offX));
      const y = Math.max(8, Math.min(window.innerHeight - rect.height - 8, ev.clientY - offY));
      setMiniPos({ x, y });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // Preload data in background after authentication
  useDataPreloader();

  // Set theme class on document
  React.useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  // Global keyboard shortcuts
  useKeyboardShortcuts([
    {
      key: 'Escape',
      action: () => {
        if (currentStream) {
          setCurrentStream(null, null);
        }
      },
      description: 'Close video player',
    },
    {
      key: '0',
      action: () => setCurrentView('home'),
      description: 'Go to Home',
    },
    {
      key: '/',
      action: () => setGlobalSearchOpen(true),
      description: 'Open search',
    },
    {
      key: '1',
      action: () => setCurrentView('live'),
      description: 'Go to Live TV',
    },
    {
      key: '2',
      action: () => setCurrentView('vod'),
      description: 'Go to Movies',
    },
    {
      key: '3',
      action: () => setCurrentView('series'),
      description: 'Go to Series',
    },
    {
      key: '4',
      action: () => setCurrentView('favorites'),
      description: 'Go to Favorites',
    },
    {
      key: '5',
      action: () => setCurrentView('settings'),
      description: 'Go to Settings',
    },
  ], isAuthenticated || isGuest);

  // Restoring a saved session.
  if (booting) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-dark-900 text-white gap-4">
        <Loader2 className="animate-spin text-primary-500" size={48} />
        <p className="text-gray-300">Restoring your session…</p>
      </div>
    );
  }

  // Operator / Admin panel is PIN-gated and usable before sign-in, so the
  // provider URL and branding can be configured without an account.
  const adminOverlay = adminPanelOpen && (
    <Suspense fallback={null}>
      <AdminPanel />
    </Suspense>
  );

  // Sign-in requested (from the reseller landing or the guest bar).
  if (!isAuthenticated && loginRequested) {
    return (
      <>
        <Login />
        {adminOverlay}
        <Toaster position="top-right" />
      </>
    );
  }

  // Not signed in and not browsing as a guest yet.
  if (!isAuthenticated && !isGuest) {
    return (
      <>
        {resellerMode ? <ResellerLanding /> : <Login />}
        {adminOverlay}
        <Toaster position="top-right" />
      </>
    );
  }

  const renderContent = () => {
    switch (currentView) {
      case 'home':
        return <Home />;
      case 'settings':
        return <Settings />;
      case 'favorites':
        return <Favorites />;
      case 'series':
        return <SeriesList />;
      case 'epg':
        return <EPGGrid />;
      default:
        return <ChannelList />;
    }
  };

  return (
    <div className={`flex flex-col h-screen overflow-hidden ${
      theme === 'dark'
        ? 'bg-dark-900 text-white'
        : 'bg-gray-100 text-gray-900'
    }`}>
      {/* Guest preview bar (reseller mode) */}
      {isGuest && (
        <div className="bg-primary-600 text-white text-sm px-4 py-2 flex items-center justify-center gap-4 flex-wrap">
          <span>You're previewing the {brandName} catalog.</span>
          <button
            onClick={() => subscribeUrl && window.open(subscribeUrl, '_blank', 'noopener')}
            className="underline font-semibold hover:text-white/90 focus-strong"
          >
            Subscribe
          </button>
          <button
            onClick={() => setLoginRequested(true)}
            className="underline hover:text-white/90 focus-strong"
          >
            Sign in
          </button>
        </div>
      )}

      {/* Top Navigation */}
      <TopNav />

      {/* Horizontal Category Bar — hidden while a channel is playing so the
          video gets the full width and the watch view stays uncluttered. */}
      {!currentStream && <HorizontalCategoryBar />}

      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Video Player Section — inline (full width) or a draggable mini window.
            Same VideoPlayer instance in both, so audio/playback never interrupt. */}
        {currentStream && currentStreamUrl && (() => {
          const inPip = !!docPip.pipWindow;
          const playerEl = (
            <VideoErrorBoundary>
              <Suspense
                fallback={
                  <div className="w-full aspect-video flex items-center justify-center bg-black">
                    <Loader2 className="animate-spin text-primary-500" size={40} />
                  </div>
                }
              >
                <VideoPlayer
                  key={currentStreamUrl}
                  src={currentStreamUrl}
                  poster={currentStream.stream_icon}
                  autoPlay={true}
                  isLive={currentStream.stream_type === 'live'}
                  startTime={playerStartTime}
                  onProgress={handleProgress}
                  onEnded={handleEnded}
                  reloadSrc={
                    currentStream.stream_type === 'live'
                      ? () => xtreamAPI.buildLiveStreamUrl(currentStream.stream_id, 'm3u8')
                      : undefined
                  }
                  onError={(error) => {
                    console.error('Playback error:', error);
                  }}
                  title={currentStream.name}
                  onOpenEpg={() => setEpgOverlayOpen(true)}
                  onClose={() => setCurrentStream(null, null)}
                  dock={inPip ? 'inline' : playerDock}
                  onToggleDock={() => setPlayerDock((d) => (d === 'mini' ? 'inline' : 'mini'))}
                  onHeaderPointerDown={startMiniDrag}
                  onPopOut={docPip.supported && !inPip ? handlePopOut : undefined}
                  isPip={inPip}
                  onClosePip={docPip.close}
                />
              </Suspense>
            </VideoErrorBoundary>
          );

          // Popped out: render the player inside the PiP window, and leave a
          // slim placeholder in the tab so the user can bring it back.
          if (inPip && docPip.pipWindow) {
            return (
              <>
                {createPortal(
                  <div className="w-screen h-screen bg-black relative">{playerEl}</div>,
                  docPip.pipWindow.document.body
                )}
                <div className="relative bg-black/60 border-b border-white/10 px-4 py-3 flex items-center justify-between gap-3">
                  <span className="text-sm text-gray-300 truncate">
                    ▶ Playing “{currentStream.name}” in a pop-out window
                  </span>
                  <button
                    onClick={docPip.close}
                    className="shrink-0 text-xs px-3 py-1.5 rounded-lg border border-white/15 text-white hover:border-primary-400 transition-colors focus-strong"
                  >
                    Bring back
                  </button>
                </div>
              </>
            );
          }

          return (
            <div
              ref={playerBoxRef}
              className={
                playerDock === 'mini'
                  ? 'fixed z-50 w-[clamp(280px,28vw,460px)] rounded-lg overflow-hidden border border-white/10 shadow-2xl bg-black'
                  : 'relative bg-black'
              }
              style={
                playerDock === 'mini'
                  ? miniPos
                    ? { left: miniPos.x, top: miniPos.y }
                    : { right: 16, bottom: 16 }
                  : undefined
              }
            >
              {playerEl}
            </div>
          );
        })()}

        {/* MultiView grid */}
        {(showMultiView || multiViewStreams.length > 0) && (
          <Suspense fallback={null}>
            <MultiViewGrid
              items={multiViewStreams}
              onClose={() => setShowMultiView(false)}
              onRemove={removeMultiViewStream}
            />
          </Suspense>
        )}

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          <ErrorBoundary key={currentView}>
            <Suspense fallback={<ViewFallback />}>{renderContent()}</Suspense>
          </ErrorBoundary>
        </div>
      </main>

      {/* Overlays — each lazy chunk loads only when first opened. */}
      {epgOverlayOpen && (
        <Suspense fallback={null}>
          <EPGOverlay />
        </Suspense>
      )}
      {detailVod && (
        <Suspense fallback={null}>
          <MovieDetailModal />
        </Suspense>
      )}
      {globalSearchOpen && (
        <Suspense fallback={null}>
          <GlobalSearch />
        </Suspense>
      )}
      {subscribeGateOpen && (
        <Suspense fallback={null}>
          <SubscribeGate />
        </Suspense>
      )}
      {adminOverlay}

      <Toaster
        position="top-right"
        toastOptions={{
          className: '',
          style: {
            background: '#1e293b',
            color: '#fff',
            border: '1px solid #334155',
          },
          success: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </div>
  );
}

export default App;
